/**
 * Format a number, removing unnecessary trailing zeros
 * e.g., 3.0 -> "3", 3.1 -> "3.1", 3.10 -> "3.1", 50.00 -> "50"
 */
function stripTrailingZeros(num, decimals = 1) {
  const fixed = Number(num).toFixed(decimals);
  // parseFloat removes trailing zeros: "3.10" -> 3.1 -> "3.1", "3.00" -> 3 -> "3"
  return parseFloat(fixed).toString();
}

/**
 * Format large numbers with K/M/B/T/Q suffix (dashboard-level compact format)
 * e.g., 1500 -> "1.5K", 1000000 -> "1M", 1500000000 -> "1.5B"
 * Supports up to Quadrillion
 */
export function formatNumber(num) {
  if (!num && num !== 0) return '0';
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e15) return sign + stripTrailingZeros(absNum / 1e15) + 'Q';
  if (absNum >= 1e12) return sign + stripTrailingZeros(absNum / 1e12) + 'T';
  if (absNum >= 1e9) return sign + stripTrailingZeros(absNum / 1e9) + 'B';
  if (absNum >= 1e6) return sign + stripTrailingZeros(absNum / 1e6) + 'M';
  if (absNum >= 10000) return sign + stripTrailingZeros(absNum / 1000) + 'K';
  if (absNum >= 1000) return sign + num.toLocaleString('en-US');
  return num.toString();
}

/**
 * Format numbers with full comma separators (for reports/tables)
 * e.g., 1000 -> "1,000", 1000000 -> "1,000,000"
 */
export function formatFullNumber(num) {
  if (!num && num !== 0) return '0';
  return Number(num).toLocaleString('en-US');
}

/**
 * Format a percentage value
 * e.g., 3.0 -> "3%", 3.14 -> "3.14%", 0 -> "0%"
 * Pass fixed=true to always keep `decimals` places, e.g. 0 -> "0.00%", 2.5 -> "2.50%"
 */
export function formatPercent(num, decimals = 1, fixed = false) {
  if (!num && num !== 0) return fixed ? Number(0).toFixed(decimals) + '%' : '0%';
  if (fixed) return Number(num).toFixed(decimals) + '%';
  return stripTrailingZeros(num, decimals) + '%';
}

/**
 * Format a group's share of the whole for distribution charts/legends.
 * Shows "<1%" for a real-but-tiny group whose share rounds down to 0%, so a
 * non-zero count is never displayed as "0%". Pass the (possibly pre-rounded)
 * percentage and the raw count so a rounded-down value can be recovered.
 * e.g., (0, 3) -> "<1%", (0, 0) -> "0%", (35, 120) -> "35%"
 */
export function formatShare(percentage, count) {
  const pct = Number(percentage) || 0;
  if (pct >= 1) return Math.round(pct) + '%';
  if (pct > 0) return '<1%';
  return Number(count) > 0 ? '<1%' : '0%';
}

/**
 * Format CTR (Click Through Rate)
 * e.g., 2.50 -> "2.5%", 3.00 -> "3%"
 */
export function formatCtr(clicks, impressions) {
  if (!impressions || impressions === 0) return '0%';
  const ctr = (clicks / impressions) * 100;
  return formatPercent(ctr, 2);
}

/**
 * Format file size
 * e.g., 1024 -> "1 KB", 1536 -> "1.5 KB"
 */
export function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes >= 1048576) return stripTrailingZeros(bytes / 1048576) + ' MB';
  if (bytes >= 1024) return stripTrailingZeros(bytes / 1024) + ' KB';
  return bytes + ' B';
}

/**
 * Format currency
 */
export function formatCurrency(amount, currency = 'USD') {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}
